import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit2, Plus, Users, UserCheck, Clock, FileText, Link as LinkIcon,
  FolderKanban, CheckCircle, AlertCircle, Trash2, ExternalLink, Megaphone,
  ClipboardList, Heart, Microscope, TrendingUp, Palette, ChevronRight, Eye, Send, X
} from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { UserAvatar, ClickableName } from '../components/UserAvatar';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimension configuration
const DIMENSIONS = [
  { key: 'cness_tasks', name: 'Tasks', icon: ClipboardList, color: '#7077FE', path: '/dashboard/cness-tasks' },
  { key: 'self_care', name: 'Self Care', icon: Heart, color: '#4CAF50', path: '/dashboard/self-care' },
  { key: 'rnd', name: 'R&D', icon: Microscope, color: '#FF9800', path: '/dashboard/rnd' },
  { key: 'personal_growth', name: 'Personal Growth', icon: TrendingUp, color: '#9C27B0', path: '/dashboard/personal-growth' },
  { key: 'creative', name: 'Creative', icon: Palette, color: '#E91E63', path: '/dashboard/creative' }
];

const ProjectView = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [project, setProject] = useState(null);
  const [subprojects, setSubprojects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [groupedTasks, setGroupedTasks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubProjectModal, setShowSubProjectModal] = useState(false);
  const [editingSubProject, setEditingSubProject] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview, tasks, documents
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  
  const [subProjectForm, setSubProjectForm] = useState({
    title: '',
    description: '',
    team_head_ids: [],
    assigned_to: [],
    priority: 5,
    deadline: '',
    status: 'active'
  });

  const [newDocForm, setNewDocForm] = useState({
    title: '',
    url: '',
    purpose: ''
  });

  // Noticeboard state
  const [noticeboardItems, setNoticeboardItems] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    content: '',
    priority: 'normal' // 'normal', 'important', 'urgent'
  });

  const fetchNoticeboardItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}/noticeboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNoticeboardItems(response.data);
    } catch (error) {
      console.error('Failed to fetch noticeboard:', error);
    }
  }, [projectId]);

  const fetchProject = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project');
      navigate('/dashboard/projects');
    }
  }, [projectId, navigate]);

  const fetchDocuments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, [projectId]);

  const fetchSubProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}/subprojects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubprojects(response.data);
    } catch (error) {
      console.error('Failed to fetch sub-projects:', error);
    }
  }, [projectId]);

  const fetchTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, [projectId]);

  const fetchGroupedTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/by-project/${projectId}/grouped`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupedTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch grouped tasks:', error);
    }
  }, [projectId]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data || []);
      setManagers(response.data.filter(u => u.role === 'manager' || u.role === 'ceo' || u.role === 'team_lead') || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProject(), fetchSubProjects(), fetchTasks(), fetchGroupedTasks(), fetchDocuments(), fetchUsers(), fetchNoticeboardItems()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProject, fetchSubProjects, fetchTasks, fetchGroupedTasks, fetchDocuments, fetchUsers, fetchNoticeboardItems]);

  // Noticeboard CRUD
  const handleAddNotice = async (e) => {
    e.preventDefault();
    if (!noticeForm.title.trim() || !noticeForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingNotice) {
        await axios.put(`${API_URL}/projects/${projectId}/noticeboard/${editingNotice.id}`, noticeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notice updated');
      } else {
        await axios.post(`${API_URL}/projects/${projectId}/noticeboard`, noticeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notice added');
      }
      setShowNoticeModal(false);
      setEditingNotice(null);
      setNoticeForm({ title: '', content: '', priority: 'normal' });
      fetchNoticeboardItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notice');
    }
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setNoticeForm({
      title: notice.title,
      content: notice.content,
      priority: notice.priority || 'normal'
    });
    setShowNoticeModal(true);
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/projects/${projectId}/noticeboard/${noticeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notice deleted');
      fetchNoticeboardItems();
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const canManageNoticeboard = ['superadmin', 'manager', 'ceo'].includes(user?.role);

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!newDocForm.url.trim()) {
      toast.error('Document URL is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/projects/${projectId}/documents`, newDocForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document added successfully');
      setShowAddDocModal(false);
      setNewDocForm({ title: '', url: '', purpose: '' });
      fetchDocuments();
      fetchProject(); // Refresh project to get updated count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/projects/${projectId}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted successfully');
      fetchDocuments();
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleCreateSubProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingSubProject) {
        await axios.put(`${API_URL}/subprojects/${editingSubProject.id}`, subProjectForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sub-project updated successfully');
      } else {
        await axios.post(`${API_URL}/subprojects`, {
          ...subProjectForm,
          project_id: projectId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sub-project created successfully');
      }
      setShowSubProjectModal(false);
      setEditingSubProject(null);
      setSubProjectForm({
        title: '',
        description: '',
        team_head_ids: [],
        assigned_to: [],
        priority: 5,
        deadline: '',
        status: 'active'
      });
      fetchSubProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sub-project');
    }
  };

  const handleEditSubProject = (sp) => {
    setEditingSubProject(sp);
    setSubProjectForm({
      title: sp.title,
      description: sp.description || '',
      team_head_ids: sp.team_head_ids || [],
      assigned_to: sp.assigned_to || [],
      priority: sp.priority || 5,
      deadline: sp.deadline ? sp.deadline.split('T')[0] : '',
      status: sp.status || 'active'
    });
    setShowSubProjectModal(true);
  };

  const handleDeleteSubProject = async (subprojectId) => {
    if (!window.confirm('Are you sure you want to delete this sub-project?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/subprojects/${subprojectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sub-project deleted successfully');
      fetchSubProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-project');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
      case 'completed': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
      case 'on_hold': return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
      case 'archived': return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9E9E9E' };
      default: return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9E9E9E' };
    }
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return '#DC2626';
    if (priority >= 5) return '#D97706';
    return '#059669';
  };

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)' }}>Project not found</p>
      </div>
    );
  }

  const statusStyle = getStatusStyle(project.status);
  const stats = project.task_stats || { total: 0, pending: 0, in_progress: 0, completed: 0 };
  const progressPercent = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;

  return (
    <div className="content-area" data-testid="project-view">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/dashboard/projects/manage')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#7077FE',
            display: 'flex',
            alignItems: 'center'
          }}
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '8px', height: '32px', borderRadius: '4px', background: project.color || '#7077FE' }} />
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {project.name}
            </h1>
            <span style={{
              padding: '4px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              background: statusStyle.bg,
              color: statusStyle.color,
              textTransform: 'capitalize'
            }}>
              {project.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/dashboard/tasks/new?project_id=${projectId}`)}
          className="gradient-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
          data-testid="add-task-btn"
        >
          <Plus size={18} /> Add Task
        </button>
      </div>

      {/* Project Description Card */}
      {project.description && (
        <div className="card" style={{ padding: '20px', marginBottom: '24px', borderLeft: `4px solid ${project.color || '#7077FE'}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <FileText size={24} color={project.color || '#7077FE'} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Project Description
              </h3>
              <div 
                style={{ 
                  color: 'var(--text-primary)', 
                  fontSize: '15px', 
                  lineHeight: '1.7',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
                dangerouslySetInnerHTML={{ __html: project.description }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' }}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: activeTab === 'overview' ? '#7077FE' : 'transparent',
            color: activeTab === 'overview' ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: activeTab === 'tasks' ? '#7077FE' : 'transparent',
            color: activeTab === 'tasks' ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
          data-testid="tab-tasks"
        >
          Tasks by Dimension
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: activeTab === 'documents' ? '#7077FE' : 'transparent',
            color: activeTab === 'documents' ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          data-testid="tab-documents"
        >
          <LinkIcon size={16} />
          Documents ({documents.length})
        </button>
      </div>

      {/* Documents View */}
      {activeTab === 'documents' && (
        <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LinkIcon size={22} color="#7077FE" /> Project Documents
            </h3>
            {user?.role !== 'team_member' && (
              <button
                onClick={() => setShowAddDocModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 18px',
                  background: 'linear-gradient(135deg, #7077FE 0%, #4CAF50 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                data-testid="add-document-btn"
              >
                <Plus size={18} /> Add Document
              </button>
            )}
          </div>

          {documents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-tertiary)' }}>
              <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '16px' }}>No documents added yet</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Add documents to keep track of important project resources</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {documents.map((doc, idx) => {
                const docDate = doc.created_at ? new Date(doc.created_at) : null;
                const isNewDate = idx === 0 || (docDate && documents[idx - 1]?.created_at && 
                  new Date(documents[idx - 1].created_at).toDateString() !== docDate.toDateString());
                
                return (
                  <React.Fragment key={doc.id || idx}>
                    {isNewDate && docDate && (
                      <div style={{
                        padding: '8px 0',
                        borderBottom: '1px solid var(--border-color)',
                        marginTop: idx > 0 ? '12px' : 0
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'var(--text-tertiary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          <Clock size={12} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                          {docDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '16px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        borderLeft: '4px solid #7077FE'
                      }}
                      data-testid={`document-${doc.id}`}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <ExternalLink size={16} color="#7077FE" />
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#7077FE',
                              textDecoration: 'none'
                            }}
                          >
                            {doc.title || 'Untitled Document'}
                          </a>
                        </div>
                        {doc.purpose && (
                          <p style={{
                            margin: '0 0 8px 24px',
                            fontSize: '13px',
                            color: 'var(--text-secondary)',
                            lineHeight: '1.5'
                          }}>
                            <strong>Purpose:</strong> {doc.purpose}
                          </p>
                        )}
                        <div style={{ marginLeft: '24px', display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                          {doc.created_by_name && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <UserCheck size={12} /> Added by {cleanName(doc.created_by_name)}
                            </span>
                          )}
                          {docDate && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} /> {docDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                      {user?.role !== 'team_member' && (
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            padding: '8px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#EF4444'
                          }}
                          title="Delete document"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tasks by Dimension View */}
      {activeTab === 'tasks' && groupedTasks && (
        <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Tasks by Dimension ({groupedTasks.total_tasks} total)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {DIMENSIONS.map(dim => {
              const dimData = groupedTasks.dimensions?.[dim.key] || { total: 0, pending: 0, in_progress: 0, completed: 0 };
              const DimIcon = dim.icon;
              return (
                <div
                  key={dim.key}
                  onClick={() => navigate(`${dim.path}?project=${projectId}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    borderLeft: `4px solid ${dim.color}`,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  data-testid={`dimension-card-${dim.key}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <DimIcon size={24} color={dim.color} />
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>{dim.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                        {dimData.total} tasks
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', background: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' }}>
                        {dimData.pending} pending
                      </span>
                      <span style={{ padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', background: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' }}>
                        {dimData.in_progress} in progress
                      </span>
                      <span style={{ padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', background: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' }}>
                        {dimData.completed} done
                      </span>
                    </div>
                    <ChevronRight size={20} color="var(--text-tertiary)" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Grid - Only show on overview tab */}
      {activeTab === 'overview' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '24px' }}>
        {/* Left Column - Main Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Progress Section */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={20} color="#7077FE" /> Progress Overview
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Overall Completion</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{Math.round(progressPercent)}%</span>
            </div>
            <div style={{ height: '12px', background: 'var(--bg-secondary)', borderRadius: '6px', overflow: 'hidden', marginBottom: '20px' }}>
              <div style={{ height: '100%', width: `${progressPercent}%`, background: `linear-gradient(135deg, ${project.color || '#7077FE'} 0%, #4CAF50 100%)`, borderRadius: '6px', transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {[
                { label: 'Total', value: stats.total, color: 'var(--text-primary)', icon: FolderKanban },
                { label: 'Pending', value: stats.pending, color: '#F59E0B', icon: Clock },
                { label: 'In Progress', value: stats.in_progress, color: '#3B82F6', icon: AlertCircle },
                { label: 'Completed', value: stats.completed, color: '#10B981', icon: CheckCircle }
              ].map(stat => (
                <div key={stat.label} style={{ textAlign: 'center', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
                  <stat.icon size={20} style={{ color: stat.color, marginBottom: '8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Noticeboard Section - Full CRUD (Moved to top) */}
          <div className="card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Megaphone size={20} color="#F59E0B" /> Project Noticeboard
              </h3>
              {canManageNoticeboard && (
                <button
                  onClick={() => { setEditingNotice(null); setNoticeForm({ title: '', content: '', priority: 'normal' }); setShowNoticeModal(true); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: '#FEF3C7',
                    border: '1px solid #FCD34D',
                    borderRadius: '8px',
                    color: '#D97706',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                  data-testid="add-notice-btn"
                >
                  <Plus size={16} /> Add Notice
                </button>
              )}
            </div>
            
            {noticeboardItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-tertiary)' }}>
                <Megaphone size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>No notices posted yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {noticeboardItems.map(notice => {
                  const priorityColors = {
                    urgent: { bg: '#FEE2E2', border: '#EF4444', text: '#DC2626' },
                    important: { bg: '#FEF3C7', border: '#F59E0B', text: '#D97706' },
                    normal: { bg: 'var(--bg-secondary)', border: 'var(--border-color)', text: 'var(--text-primary)' }
                  };
                  const pStyle = priorityColors[notice.priority] || priorityColors.normal;
                  
                  return (
                    <div 
                      key={notice.id}
                      style={{
                        padding: '16px',
                        background: pStyle.bg,
                        border: `1px solid ${pStyle.border}`,
                        borderRadius: '10px',
                        borderLeft: notice.priority === 'urgent' ? '4px solid #EF4444' : notice.priority === 'important' ? '4px solid #F59E0B' : '4px solid var(--border-color)'
                      }}
                      data-testid={`notice-${notice.id}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: pStyle.text }}>
                              {notice.title}
                            </h4>
                            {notice.priority !== 'normal' && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                background: notice.priority === 'urgent' ? '#EF4444' : '#F59E0B',
                                color: 'white'
                              }}>
                                {notice.priority}
                              </span>
                            )}
                          </div>
                          <div 
                            style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{ __html: notice.content }}
                          />
                          <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={12} />
                            {new Date(notice.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {notice.created_by_name && (
                              <span>• by {notice.created_by_name}</span>
                            )}
                          </div>
                        </div>
                        {canManageNoticeboard && (
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button
                              onClick={() => handleEditNotice(notice)}
                              style={{ padding: '6px', background: 'var(--bg-primary)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                              title="Edit Notice"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteNotice(notice.id)}
                              style={{ padding: '6px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#EF4444' }}
                              title="Delete Notice"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sub-Projects Section */}
          <div className="card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FolderKanban size={20} color="#7077FE" /> Sub-Projects ({subprojects.length})
              </h3>
              {canCreate('subprojects') && (
                <button
                  onClick={() => {
                    setEditingSubProject(null);
                    setSubProjectForm({
                      title: '',
                      description: '',
                      team_head_ids: [],
                      assigned_to: [],
                      priority: 5,
                      deadline: '',
                      status: 'active'
                    });
                    setShowSubProjectModal(true);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: '#EEF2FF',
                    border: '1px solid #A5B4FC',
                    borderRadius: '8px',
                    color: '#4338CA',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                  data-testid="add-subproject-btn"
                >
                  <Plus size={16} /> Add Sub-Project
                </button>
              )}
            </div>

            {subprojects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                <FolderKanban size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>No sub-projects yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {subprojects.map(sp => {
                  const spStatusStyle = getStatusStyle(sp.status);
                  return (
                    <div
                      key={sp.id}
                      style={{
                        padding: '16px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '10px',
                        borderLeft: `4px solid ${getPriorityColor(sp.priority)}`
                      }}
                      data-testid={`subproject-${sp.id}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                            {sp.title}
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: spStatusStyle.bg,
                              color: spStatusStyle.color,
                              textTransform: 'capitalize'
                            }}>
                              {sp.status?.replace('_', ' ')}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: `${getPriorityColor(sp.priority)}15`,
                              color: getPriorityColor(sp.priority)
                            }}>
                              Priority: {sp.priority}
                            </span>
                            {sp.deadline && (
                              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Clock size={12} /> {new Date(sp.deadline).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => navigate(`/dashboard/projects/${projectId}/subprojects/${sp.id}`)}
                            style={{ padding: '6px', background: 'rgba(112, 119, 254, 0.1)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#7077FE' }}
                            title="View Sub-Project"
                            data-testid={`view-subproject-${sp.id}`}
                          >
                            <Eye size={14} />
                          </button>
                          {canEdit('subprojects') && (
                            <button
                              onClick={() => handleEditSubProject(sp)}
                              style={{ padding: '6px', background: 'var(--bg-primary)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                              title="Edit Sub-Project"
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {canDelete('subprojects') && (
                            <button
                              onClick={() => handleDeleteSubProject(sp.id)}
                              style={{ padding: '6px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#EF4444' }}
                              title="Delete Sub-Project"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      {sp.team_heads && sp.team_heads.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>
                            <UserCheck size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Team Heads
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {sp.team_heads.map((u, idx) => (
                              <div 
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/profile/${u.id}`); }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '4px 10px 4px 4px',
                                  background: '#EEF2FF',
                                  borderRadius: '20px',
                                  cursor: 'pointer'
                                }}
                                title={`View ${cleanName(u.full_name)}'s profile`}
                              >
                                <UserAvatar user={u} size={22} />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#4338CA' }}>{cleanName(u.full_name)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {sp.assigned_users && sp.assigned_users.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>
                            <Users size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Team Members
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {sp.assigned_users.map((u, idx) => (
                              <div 
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/profile/${u.id}`); }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '4px 10px 4px 4px',
                                  background: 'var(--bg-primary)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '20px',
                                  cursor: 'pointer'
                                }}
                                title={`View ${cleanName(u.full_name)}'s profile`}
                              >
                                <UserAvatar user={u} size={22} />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>{cleanName(u.full_name)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Team Info with Avatars */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={20} color="#7077FE" /> Project Team
            </h3>
            
            {project.team_heads && project.team_heads.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <UserCheck size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Team Heads ({project.team_heads.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {project.team_heads.map((th, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => navigate(`/dashboard/profile/${th.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        background: '#EEF2FF',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      data-testid={`team-head-${th.id}`}
                    >
                      <UserAvatar user={th} size={36} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#4338CA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cleanName(th.full_name)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6366F1', textTransform: 'capitalize' }}>
                          {th.role?.replace('_', ' ')}
                        </div>
                      </div>
                      <ChevronRight size={16} color="#6366F1" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {project.members && project.members.length > 0 && (
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <Users size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Team Members ({project.members.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {project.members.map((m, idx) => (
                    <div 
                      key={idx}
                      onClick={() => navigate(`/dashboard/profile/${m.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        border: '1px solid var(--border-color)',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      data-testid={`team-member-${m.id}`}
                    >
                      <UserAvatar user={m} size={32} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cleanName(m.full_name)}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'capitalize' }}>
                          {m.role?.replace('_', ' ')}
                        </div>
                      </div>
                      <ChevronRight size={14} color="var(--text-tertiary)" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!project.team_heads || project.team_heads.length === 0) && (!project.members || project.members.length === 0) && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>
                <Users size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '13px' }}>No team assigned yet</p>
              </div>
            )}
          </div>

          {/* Project Details */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={20} color="#7077FE" /> Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {project.deadline && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <Clock size={14} color="var(--text-tertiary)" />
                  <span style={{ color: 'var(--text-secondary)' }}>Deadline:</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{new Date(project.deadline).toLocaleDateString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <FolderKanban size={14} color="var(--text-tertiary)" />
                <span style={{ color: 'var(--text-secondary)' }}>Sub-projects:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{subprojects.length}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <CheckCircle size={14} color="var(--text-tertiary)" />
                <span style={{ color: 'var(--text-secondary)' }}>Tasks:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{tasks.length}</span>
              </div>
            </div>
          </div>

          {/* Document Links */}
          {project.document_links && project.document_links.length > 0 && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LinkIcon size={20} color="#7077FE" /> Documents
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {project.document_links.map((doc, idx) => (
                  <a
                    key={idx}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      transition: 'background 0.2s'
                    }}
                  >
                    <ExternalLink size={14} color="#7077FE" />
                    <span style={{ flex: 1 }}>{doc.title || doc.url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Sub-Project Modal */}
      {showSubProjectModal && (
        <div className="modal-overlay" onClick={() => setShowSubProjectModal(false)} data-testid="subproject-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {editingSubProject ? 'Edit Sub-Project' : 'Create Sub-Project'}
              </h2>
              <button onClick={() => setShowSubProjectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '24px' }}>×</button>
            </div>

            <form onSubmit={handleCreateSubProject}>
              {/* Title */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Title *</label>
                <input
                  type="text"
                  value={subProjectForm.title}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, title: e.target.value })}
                  required
                  placeholder="Enter sub-project title"
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="subproject-title-input"
                />
              </div>

              {/* Description (WYSIWYG) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Description</label>
                <RichTextEditor
                  value={subProjectForm.description}
                  onChange={(value) => setSubProjectForm({ ...subProjectForm, description: value })}
                  placeholder="Enter sub-project description..."
                  minHeight="120px"
                />
              </div>

              {/* Team Heads (Managers - Multi-select) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Team Heads (Managers)</label>
                <div style={{ padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', maxHeight: '150px', overflowY: 'auto' }}>
                  {managers.map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer', borderRadius: '4px' }}>
                      <input
                        type="checkbox"
                        checked={subProjectForm.team_head_ids.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubProjectForm({ ...subProjectForm, team_head_ids: [...subProjectForm.team_head_ids, m.id] });
                          } else {
                            setSubProjectForm({ ...subProjectForm, team_head_ids: subProjectForm.team_head_ids.filter(id => id !== m.id) });
                          }
                        }}
                        style={{ marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{cleanName(m.full_name)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Assigned To (All Users - Multi-select) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Assigned To (Team Members)</label>
                <div style={{ padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', maxHeight: '150px', overflowY: 'auto' }}>
                  {allUsers.filter(u => u.role !== 'educator').map((u) => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer', borderRadius: '4px' }}>
                      <input
                        type="checkbox"
                        checked={subProjectForm.assigned_to.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubProjectForm({ ...subProjectForm, assigned_to: [...subProjectForm.assigned_to, u.id] });
                          } else {
                            setSubProjectForm({ ...subProjectForm, assigned_to: subProjectForm.assigned_to.filter(id => id !== u.id) });
                          }
                        }}
                        style={{ marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{cleanName(u.full_name)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority Slider */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                  Priority: <span style={{ color: getPriorityColor(subProjectForm.priority), fontWeight: '700' }}>{subProjectForm.priority}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={subProjectForm.priority}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, priority: parseInt(e.target.value) })}
                  style={{ width: '100%', cursor: 'pointer' }}
                  data-testid="subproject-priority-slider"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                  <span>Low (1)</span>
                  <span>High (10)</span>
                </div>
              </div>

              {/* Deadline */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Deadline / ETA</label>
                <input
                  type="date"
                  value={subProjectForm.deadline}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, deadline: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="subproject-deadline-input"
                />
              </div>

              {/* Status */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Status</label>
                <select
                  value={subProjectForm.status}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, status: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="subproject-status-select"
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowSubProjectModal(false)}
                  style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px' }}
                  data-testid="subproject-submit-btn"
                >
                  {editingSubProject ? 'Update Sub-Project' : 'Create Sub-Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {showAddDocModal && (
        <div className="modal-overlay" onClick={() => setShowAddDocModal(false)} data-testid="add-document-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LinkIcon size={22} color="#7077FE" /> Add Document
              </h2>
              <button onClick={() => setShowAddDocModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '24px' }}>×</button>
            </div>

            <form onSubmit={handleAddDocument}>
              {/* Title */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Document Title *</label>
                <input
                  type="text"
                  value={newDocForm.title}
                  onChange={(e) => setNewDocForm({ ...newDocForm, title: e.target.value })}
                  required
                  placeholder="e.g., Project Requirements Document"
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="doc-title-input"
                />
              </div>

              {/* URL */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Document URL *</label>
                <input
                  type="url"
                  value={newDocForm.url}
                  onChange={(e) => setNewDocForm({ ...newDocForm, url: e.target.value })}
                  required
                  placeholder="https://docs.google.com/..."
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="doc-url-input"
                />
              </div>

              {/* Purpose */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Purpose / Description</label>
                <textarea
                  value={newDocForm.purpose}
                  onChange={(e) => setNewDocForm({ ...newDocForm, purpose: e.target.value })}
                  placeholder="Describe what this document is for and when to use it..."
                  rows={3}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical' }}
                  data-testid="doc-purpose-input"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddDocModal(false)}
                  style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px' }}
                  data-testid="doc-submit-btn"
                >
                  Add Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Noticeboard Modal */}
      {showNoticeModal && (
        <div className="modal-overlay" onClick={() => setShowNoticeModal(false)} data-testid="notice-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Megaphone size={22} color="#F59E0B" /> {editingNotice ? 'Edit Notice' : 'Add Notice'}
              </h2>
              <button 
                onClick={() => setShowNoticeModal(false)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddNotice}>
              {/* Title */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Notice Title *</label>
                <input
                  type="text"
                  value={noticeForm.title}
                  onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                  required
                  placeholder="e.g., Important Update, Team Meeting, etc."
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="notice-title-input"
                />
              </div>

              {/* Priority */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Priority</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {['normal', 'important', 'urgent'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNoticeForm({ ...noticeForm, priority: p })}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: `2px solid ${noticeForm.priority === p ? (p === 'urgent' ? '#EF4444' : p === 'important' ? '#F59E0B' : '#7077FE') : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        background: noticeForm.priority === p ? (p === 'urgent' ? '#FEE2E2' : p === 'important' ? '#FEF3C7' : '#EEF2FF') : 'transparent',
                        color: noticeForm.priority === p ? (p === 'urgent' ? '#DC2626' : p === 'important' ? '#D97706' : '#4338CA') : 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        textTransform: 'capitalize'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Notice Content *</label>
                <RichTextEditor
                  value={noticeForm.content}
                  onChange={(value) => setNoticeForm({ ...noticeForm, content: value })}
                  placeholder="Write your notice content here..."
                  minHeight="150px"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowNoticeModal(false)}
                  style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  data-testid="notice-submit-btn"
                >
                  <Send size={16} />
                  {editingNotice ? 'Update Notice' : 'Post Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;
